import useRouter from "common/hooks/use-router";
import useAuth from "hooks/useAuth";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Plot } from "types";
import type { TableColumn } from "common/components/DataTable/DataTable";
import CrudList from "common/components/Crud/CrudList";
import type { CrudFilter } from "common/components/Crud/components/CrudFilters";
import CrudEdit from "common/components/Crud/CrudEdit";
import PlotForm from "./PlotForm";
import { CrudHeaderAction } from "common/components/Crud/components/CrudHeader";
import { Calendar } from "primereact/calendar";

function DateFilter({ value, onChange, ...props }: any) {
  return (
    <Calendar
      {...props}
      value={value ?? null}
      onChange={(e) => onChange(e.value ?? null)}
    />
  );
}

function SidebarPlotForm({ ...props }: any) {
  return <CrudEdit collectionName="plots" Form={PlotForm} {...props} />;
}

function Plots() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { user: currentUser } = useAuth();

  const handleDetail = useCallback(
    (id: any) => {
      navigate(`/plots/${id}`);
    },
    [navigate]
  );

  const columns: TableColumn<Plot>[] = useMemo(
    () => [
      {
        field: "name",
        header: t("plots.name"),
        sortable: true,
        renderType: "bold",
      },
      {
        field: "variety",
        header: t("plots.variety"),
        sortable: true,
      },
      {
        field: "step",
        header: t("plots.step"),
        sortable: true,
        customRender: (rowData: Plot) =>
          rowData.step != null
            ? `Étape ${rowData.step}`
            : "-",
      },
      {
        field: "stepDate",
        header: t("plots.stepDate"),
        sortable: true,
        customRender: (rowData: Plot) =>
          rowData.stepDate
            ? new Date(rowData.stepDate).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "-",
      },
      {
        field: "ricePaddy",
        header: t("plots.ricepaddy"),
        customRender: (rowData: Plot) => rowData.ricePaddy?.name ?? "—",
      },
    ],
    [t]
  );

  const filters: CrudFilter[] = useMemo(() => {
    return [
      {
        name: "name$containsi",
        label: t("plots.name"),
        renderType: "input",
      },
      {
        name: "variety$containsi",
        label: t("plots.variety"),
        renderType: "input",
      },
      {
        name: "stepDate$date",
        label: t("plots.stepDate"),
        renderType: "custom",
        renderComponent: (
          <DateFilter
            dateFormat="dd/mm/yy"
            locale="fr"
            showButtonBar
            inputId="stepDate$date"
          />
        ),
      },
    ];
  }, [t]);

  return (
    <CrudList
      collectionName="plots"
      columns={columns}
      filters={filters}
      defaultSort="name:ASC"
      populate={["ricePaddy"]}
      canEdit={false}
      canDelete={false}
      icon="MapPinIcon"
      onNavigate={handleDetail}
      actionsHeader=""
      SidebarFormComponent={SidebarPlotForm}
      withMapView
      getMarkerPosition={(item: Plot) =>
        item.ricePaddy?.lat != null && item.ricePaddy?.lng != null
          ? { lat: item.ricePaddy.lat, lng: item.ricePaddy.lng }
          : null
      }
      getMarkerLabel={(item) => item.name ?? `#${item.id}`}
    />
  );
}

export default Plots;
